/**
 * Central Experience Widget Registry
 * 
 * Single source of truth for all experience view widgets.
 * Widget IDs in DocViewSchema.sections[].widgetId must match keys here.
 * 
 * Rules of Engagement:
 * - widgetId in schema must match EXACTLY with a key in EXPERIENCE_WIDGETS
 * - No implicit naming conventions - explicit key matching only
 * - Missing widgets log warnings in dev, render placeholder or skip in prod
 * 
 * To add a new widget:
 * 1. Create the widget component in the appropriate feature folder
 * 2. Import and add to EXPERIENCE_WIDGETS below
 * 3. Use the widgetId in your DocViewSchema section
 */

import React from "react";

// Item widgets
import { ProductGalleryWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ProductGalleryWidget";
import { InventorySection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/InventorySection";
import { ProductDetailsSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ProductDetailsSection";
import { ItemVariationsTabWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ItemVariationsTabWidget";
import { ItemCreateOverviewWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ItemCreateOverviewWidget";
import { ItemTemplateToggleWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ItemTemplateToggleWidget";
import { ProductDocumentsWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ProductDocumentsWidget";
import { ItemPersonalizationWidget } from "@/features/customization/ItemPersonalizationWidget";
import { ItemVariantsSummaryWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ItemVariantsSummaryWidget";
import { BundleComponentsSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/BundleComponentsSection";
import { BundleToggleWidget } from "@/features/inventory/item-detail/components/experience-views/item/widgets/BundleToggleWidget";

// Item Customization Template widgets (Plan 126)
import { TemplateInfoWidget } from "@/features/customization/widgets/TemplateInfoWidget";
import { TemplateFieldBuilderWidget } from "@/features/customization/widgets/TemplateFieldBuilderWidget";
import { TemplateRulesWidget } from "@/features/customization/widgets/TemplateRulesWidget";
import { TemplateLinkedProductsWidget } from "@/features/customization/widgets/TemplateLinkedProductsWidget";

// Item Group widgets
import { ItemGroupStorefrontSection } from "@/features/inventory/item-detail/components/experience-views/item-group/widgets/ItemGroupStorefrontSection";

// Generic fallback widget
import { GenericSection } from "@/features/inventory/item-detail/components/experience-views/sales-order";

// Pricing widgets
import { PricingCard } from "@/features/inventory/item-detail/components/experience-views/item/widgets/PricingCard";

// Storefront widgets
import { StorefrontThemeEditorWidget } from "@/features/storefront/theme-tokens/StorefrontThemeEditorWidget";
import { StorefrontPageBuilderWidget } from "@/features/storefront/widgets/StorefrontPageBuilderWidget";
import { StorefrontSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/StorefrontSection";
import { ItemCollectionsSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/ItemCollectionsSection";
import { AdvancedSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/AdvancedSection";
import { DisabledBanner } from "@/features/inventory/item-detail/components/experience-views/item/widgets/DisabledBanner";
import { MarketplaceListingsSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/MarketplaceListingsSection";
import { BookingSection } from "@/features/inventory/item-detail/components/experience-views/item/widgets/BookingSection";

// Marketplace listing detail widgets
import { MarketplaceSyncStatusWidget } from "@/features/channels/widgets/MarketplaceSyncStatusWidget";
import { MarketplaceImagesWidget } from "@/features/channels/widgets/MarketplaceImagesWidget";
import { MarketplaceProductInfoWidget } from "@/features/channels/widgets/MarketplaceProductInfoWidget";
import { MarketplacePricingWidget } from "@/features/channels/widgets/MarketplacePricingWidget";
import { MarketplacePackageWidget } from "@/features/channels/widgets/MarketplacePackageWidget";
import { MarketplaceInventoryWidget } from "@/features/channels/widgets/MarketplaceInventoryWidget";
import { MarketplaceLinkedItemWidget } from "@/features/channels/widgets/MarketplaceLinkedItemWidget";
import { MarketplaceDescriptionWidget } from "@/features/channels/widgets/MarketplaceDescriptionWidget";
import { LazadaRejectReasonsWidget } from "@/features/channels/widgets/LazadaRejectReasonsWidget";
import { TikTokEditOverlayWidget } from "@/features/channels/widgets/TikTokEditOverlayWidget";
import { ProductAttributesCard } from "@/features/channels/widgets/ProductAttributesCard";
import { TikTokDangerZoneWidget } from "@/features/channels/widgets/TikTokDangerZoneWidget";

// Stock Entry widgets (create wizard)
import { StockEntryTypePickerWidget } from "@/features/stock-entry/widgets/StockEntryTypePickerWidget";
import { StockEntryWarehouseWidget } from "@/features/stock-entry/widgets/StockEntryWarehouseWidget";
import { StockEntryItemsWidget } from "@/features/stock-entry/widgets/StockEntryItemsWidget";

// Pricing Rule widgets (create wizard)
import { PricingRuleTypePickerWidget } from "@/features/inventory/pricing-rule/PricingRuleTypePickerWidget";
import { PricingRuleDiscountWidget } from "@/features/inventory/pricing-rule/PricingRuleDiscountWidget";
import { PricingRuleScopeWidget } from "@/features/inventory/pricing-rule/PricingRuleScopeWidget";
import { PricingRuleAppliesToWidget } from "@/features/inventory/pricing-rule/PricingRuleAppliesToWidget";
import { PricingRuleTargetingWidget } from "@/features/inventory/pricing-rule/PricingRuleTargetingWidget";
import { PricingRuleRewardWidget } from "@/features/inventory/pricing-rule/PricingRuleRewardWidget";
import { PricingRuleConditionsWidget } from "@/features/inventory/pricing-rule/PricingRuleConditionsWidget";

// NOTE: Sales Order widgets are NOT registered here.
// Sales Order uses the legacy FormViewConfig approach with SALES_ORDER_WIDGETS registry
// in src/features/inventory/item-detail/components/experience-views/sales-order/index.ts
// See the explanation in docViewSchemas/index.ts for why Sales Order is not in DocViewSchema.

// Re-export types for widget props
export type { ShopifySectionWidgetProps } from "@/features/inventory/item-detail/components/experience-views/item/types";

/**
 * Widget props type - all widgets receive these base props.
 * This is the existing ShopifySectionWidgetProps for backward compatibility.
 */
export type ExperienceWidgetProps = {
  section: any;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void;
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  isSaving?: boolean;
  resetKey?: string | number;
  metaFields?: any[];
  viewMode?: "system" | "experience";
};

/**
 * Central registry of experience view widgets.
 * 
 * Keys must match widgetId values used in DocViewSchema sections.
 * Values are React components that render the section UI.
 * 
 * Naming convention: {DocType}{SectionName}Widget
 * Examples:
 * - ItemGalleryWidget
 * - ItemInventoryWidget
 * - SalesOrderItemsWidget
 */
export const EXPERIENCE_WIDGETS: Record<
  string,
  React.ComponentType<ExperienceWidgetProps>
> = {
  // Item widgets
  ItemGalleryWidget: ProductGalleryWidget,
  ItemOverviewWidget: ProductGalleryWidget,
  ItemCreateOverviewWidget: ItemCreateOverviewWidget, // For create mode (draft)
  ItemTemplateToggleWidget: ItemTemplateToggleWidget, // Template mode toggle for create mode
  ItemProductDetailsWidget: ProductDetailsSection,
  ItemInventoryWidget: InventorySection,
  ItemVariationsTabWidget: ItemVariationsTabWidget,
  ItemDocumentsWidget: ProductDocumentsWidget,
  ItemPersonalizationWidget: ItemPersonalizationWidget,
  ItemVariantsSummaryWidget: ItemVariantsSummaryWidget,
  ItemBundleComponentsWidget: BundleComponentsSection,
  ItemBundleToggleWidget: BundleToggleWidget,

  // Item Group widgets
  ItemGroupStorefrontWidget: ItemGroupStorefrontSection,

  // Generic fallback (for sections that just render fields)
  GenericSection: GenericSection,

  // Storefront widgets
  StorefrontThemeEditorWidget: StorefrontThemeEditorWidget,
  StorefrontPageBuilderWidget: StorefrontPageBuilderWidget,
  ItemStorefrontWidget: StorefrontSection,
  ItemCollectionsWidget: ItemCollectionsSection,
  ItemPricingWidget: PricingCard,
  ItemAdvancedWidget: AdvancedSection,
  ItemDisabledBannerWidget: DisabledBanner,
  ItemMarketplaceListingsWidget: MarketplaceListingsSection,
  ItemBookingWidget: BookingSection,

  // Marketplace listing detail widgets
  MarketplaceSyncStatusWidget: MarketplaceSyncStatusWidget,
  MarketplaceImagesWidget: MarketplaceImagesWidget,
  MarketplaceProductInfoWidget: MarketplaceProductInfoWidget,
  MarketplacePricingWidget: MarketplacePricingWidget,
  MarketplaceInventoryWidget: MarketplaceInventoryWidget,
  MarketplacePackageWidget: MarketplacePackageWidget,
  MarketplaceLinkedItemWidget: MarketplaceLinkedItemWidget,
  MarketplaceDescriptionWidget: MarketplaceDescriptionWidget,
  LazadaRejectReasonsWidget: LazadaRejectReasonsWidget,
  TikTokEditOverlayWidget: TikTokEditOverlayWidget,
  TikTokDangerZoneWidget: TikTokDangerZoneWidget,
  ProductAttributesCard: ProductAttributesCard,

  // Item Customization Template widgets (Plan 126)
  TemplateInfoWidget: TemplateInfoWidget,
  TemplateFieldBuilderWidget: TemplateFieldBuilderWidget,
  TemplateRulesWidget: TemplateRulesWidget,
  TemplateLinkedProductsWidget: TemplateLinkedProductsWidget,

  // Stock Entry widgets (create wizard)
  StockEntryTypePickerWidget: StockEntryTypePickerWidget,
  StockEntryWarehouseWidget: StockEntryWarehouseWidget,
  StockEntryItemsWidget: StockEntryItemsWidget,

  // Pricing Rule widgets (create wizard)
  PricingRuleTypePickerWidget: PricingRuleTypePickerWidget,
  PricingRuleDiscountWidget: PricingRuleDiscountWidget,
  PricingRuleScopeWidget: PricingRuleScopeWidget,
  PricingRuleAppliesToWidget: PricingRuleAppliesToWidget,
  PricingRuleTargetingWidget: PricingRuleTargetingWidget,
  PricingRuleRewardWidget: PricingRuleRewardWidget,
  PricingRuleConditionsWidget: PricingRuleConditionsWidget,

  // NOTE: Sales Order widgets are registered in SALES_ORDER_WIDGETS
  // (legacy FormViewConfig path), not here.
};

/**
 * Get a widget component by its ID.
 * 
 * @param widgetId - The widget ID from DocViewSchema
 * @returns The widget component, or null if not found
 * 
 * In dev: logs warning if widget not found
 * In prod: returns null silently
 */
export function getExperienceWidget(
  widgetId: string
): React.ComponentType<ExperienceWidgetProps> | null {
  const widget = EXPERIENCE_WIDGETS[widgetId];

  if (!widget) {
    const message = `[ExperienceWidgets] No widget registered for id "${widgetId}". ` +
      `Add it to EXPERIENCE_WIDGETS in src/lib/views/widgets/registry.ts`;

    if (import.meta.env.DEV) {
      console.warn(message);
    }

    return null;
  }

  return widget;
}

/**
 * Check if a widget is registered.
 */
export function hasExperienceWidget(widgetId: string): boolean {
  return widgetId in EXPERIENCE_WIDGETS;
}

/**
 * Get list of all registered widget IDs.
 * Useful for debugging and validation.
 */
export function getRegisteredWidgetIds(): string[] {
  return Object.keys(EXPERIENCE_WIDGETS);
}

